import React from "react";
import { useShow, useList } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Card, Table, Tag, Tabs, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
    AppstoreOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    TagOutlined,
    DollarOutlined,
    InboxOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/ui";
import type { IProduct } from "../../interfaces/product";
import { StockMovementSummary } from "../inventory/components/StockMovementSummary";

export const ProductShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow<IProduct>({
        resource: "Product",
        id,
    });

    const { data, isLoading } = queryResult;
    const record = data?.data;

    // Use prices from the product record if available (included in the response)
    // This avoids the separate API call that was returning all prices
    const productPrices = record?.prices || [];

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: (
                <div className="space-y-4">
                    {/* Basic Information Card */}
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Basic Information</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Item Code:</span>
                                    <span className="info-value font-semibold">{record?.itemCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Product Name:</span>
                                    <span className="info-value">{record?.itemName || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Type/Description:</span>
                                    <span className="info-value">{record?.itemTypeCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">HS Code/Classification:</span>
                                    <span className="info-value">{record?.itemClassificationCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Barcode:</span>
                                    <span className="info-value">{record?.barcode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Status:</span>
                                    <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                        {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                        {record?.isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Created At:</span>
                                    <span className="info-value">
                                        <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Last Updated:</span>
                                    <span className="info-value">
                                        <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tax Information Card */}
                    {record?.tax && (
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">
                                    <TagOutlined style={{ marginRight: 8 }} />
                                    Tax Information
                                </h3>
                            </div>
                            <div className="info-card-body">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Tax Type:</span>
                                        <span className="info-value">{record.tax.taxType}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Tax Rate:</span>
                                        <span className="info-value font-semibold text-primary">{record.tax.rate}%</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Tax Status:</span>
                                        <Tag color={record.tax.isActive ? "success" : "default"}>
                                            {record.tax.isActive ? "Active" : "Inactive"}
                                        </Tag>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Additional Information Card */}
                    {record?.additionalInformation && (
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">Additional Information</h3>
                            </div>
                            <div className="info-card-body">
                                <p className="text-text-secondary">{record.additionalInformation}</p>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'prices',
            label: (
                <span>
                    <DollarOutlined style={{ marginRight: 8 }} />
                    Prices
                </span>
            ),
            children: (
                <Card className="data-table-card">
                    <Table
                        dataSource={productPrices}
                        rowKey="id"
                        pagination={false}
                        className="custom-table"
                        scroll={{ x: 600 }}
                    >
                        <Table.Column
                            title="Currency"
                            dataIndex="currencyCode"
                            key="currencyCode"
                            render={(value: string) => (
                                <span className="font-medium">{value || "USD"}</span>
                            )}
                        />
                        <Table.Column
                            title="Base Price"
                            dataIndex="basePrice"
                            key="basePrice"
                            render={(value: number, record: any) => (
                                <span className="text-text-secondary">
                                    {record.currencyCode || '$'} {value?.toFixed(2)}
                                </span>
                            )}
                        />
                        <Table.Column
                            title="Effective Price"
                            dataIndex="effectivePrice"
                            key="effectivePrice"
                            render={(value: number, record: any) => (
                                <span className="font-semibold text-primary">
                                    {record.currencyCode || '$'} {value?.toFixed(2)}
                                </span>
                            )}
                        />
                        <Table.Column
                            title="Min Qty"
                            dataIndex="minimumQuantity"
                            key="minimumQuantity"
                            render={(value: number) => value || "-"}
                            responsive={['md']}
                        />
                        <Table.Column
                            title="Status"
                            dataIndex="isActive"
                            key="status"
                            render={(value: boolean) => (
                                <Tag color={value ? "success" : "default"}>
                                    {value ? "Active" : "Inactive"}
                                </Tag>
                            )}
                        />
                    </Table>
                </Card>
            ),
        },
        {
            key: 'stock',
            label: (
                <span>
                    <InboxOutlined style={{ marginRight: 8 }} />
                    Stock Movements
                </span>
            ),
            children: record?.id ? (
                <StockMovementSummary productId={record.id} />
            ) : (
                <div className="text-center py-8 text-text-muted">No stock data available</div>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title={record?.itemName || "Product Details"}
                titleIcon={<AppstoreOutlined />}
                breadcrumbs={[
                    { title: "Products", href: "/products" },
                    { title: record?.itemName || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/products/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Item Code</div>
                        <div className="mini-stat-value">{record?.itemCode || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Type</div>
                        <div className="mini-stat-value">{record?.itemTypeCode || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                            {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {record?.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                    {record?.tax && (
                        <div className="mini-stat-card">
                            <div className="mini-stat-label">Tax Rate</div>
                            <div className="mini-stat-value">{record.tax.rate}%</div>
                        </div>
                    )}
                </div>
            </PageHeader>

            {/* Tabs */}
            <div className="tab-card">
                <Tabs defaultActiveKey="overview" items={tabItems} className="custom-tabs" />
            </div>
        </div>
    );
};

export default ProductShow;
